import { baseCSS, openPrint, fmt } from './workflowPDF';
import { identidadeDoLaboratorio, linhaDoResponsavel } from './laboratorioNoRelatorio';
import {
  saldoDeEmbrioes, saldoDeSemen, resumoDoEstoqueDoCliente,
  extratoDeEstoque, extratoFecha, temDataAproximada,
} from './calculations/extratoDeEstoque';

/**
 * O EXTRATO DE ESTOQUE DE UM CLIENTE, em papel.
 *
 * É o documento que responde ao telefonema: "quantos embriões meus estão aí?".
 * A resposta falada não se sustenta — o cliente anota errado, ou lembra de um
 * número que era o do ano passado —, e até aqui a única forma de mandar era
 * escrever no WhatsApp de cabeça.
 *
 * Duas seções, na ordem em que a pergunta vem:
 *
 *   O QUE TEM   o saldo de hoje. É a resposta, e por isso vem primeiro e em
 *               corpo grande.
 *   EXTRATO     o que entrou e o que saiu. É a defesa do número de cima, para
 *               quando ele não bate com o que o cliente lembra.
 *
 * Sem endereço de botijão, como no portal: é mapa interno do laboratório, muda
 * quando alguém arruma o botijão, e este papel vai para a mão do cliente.
 */

const num = (v) => Number(v || 0).toLocaleString('pt-BR');

/** Dose aceita meia, e "3,5 doses" é como se fala. */
const doses = (v) => {
  const n = Number(v) || 0;
  return Number.isInteger(n) ? String(n) : n.toFixed(2).replace('.', ',');
};

const escapa = (v) => String(v ?? '')
  .replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');

const cabecalho = (cliente, laboratorio) => `
  <div class="page-header">
    ${identidadeDoLaboratorio({ nome: laboratorio?.name })}
    <div class="header-right">
      <div class="client">${escapa(cliente?.name || 'Cliente')}</div>
      <div class="meta">
        Posição de ${new Date().toLocaleDateString('pt-BR')}
      </div>
    </div>
  </div>`;

const secaoEmbrioes = (congelados) => {
  const grupos = saldoDeEmbrioes(congelados);
  if (grupos.length === 0) return '';

  return `
    <div class="section-title">Embriões congelados</div>
    <table>
      <thead>
        <tr>
          <th>Doadora</th><th>Touro</th><th>Estágio</th><th>Método</th>
          <th style="text-align:right">Palhetas</th>
        </tr>
      </thead>
      <tbody>
        ${grupos.map(g => `
          <tr>
            <td>${escapa(g.doadora)}</td>
            <td>${escapa(g.touro)}</td>
            <td class="dim">${escapa(g.estagios.join(', ') || '—')}</td>
            <td class="dim">${escapa(g.metodos.join(', ') || '—')}</td>
            <td style="text-align:right" class="num">${num(g.quantidade)}</td>
          </tr>`).join('')}
      </tbody>
      <tfoot>
        <tr>
          <td colspan="4">Total no botijão</td>
          <td style="text-align:right">${num(congelados.length)}</td>
        </tr>
      </tfoot>
    </table>`;
};

const secaoSemen = (partidas) => {
  const linhas = saldoDeSemen(partidas);
  if (linhas.length === 0) return '';

  const total = linhas.reduce((t, p) => t + p.doses, 0);
  return `
    <div class="section-title" style="margin-top:14px">Sêmen</div>
    <table>
      <thead>
        <tr>
          <th>Touro</th><th>Lote</th><th>Fornecedor</th><th>Desde</th>
          <th style="text-align:right">Doses</th>
        </tr>
      </thead>
      <tbody>
        ${linhas.map(p => `
          <tr>
            <td>${escapa(p.touro)}</td>
            <td class="dim">${escapa(p.codigo || '—')}</td>
            <td class="dim">${escapa(p.fornecedor || '—')}</td>
            <td class="dim">${p.desde ? fmt(p.desde) : '—'}</td>
            <td style="text-align:right" class="num">${doses(p.doses)}</td>
          </tr>`).join('')}
      </tbody>
      <tfoot>
        <tr>
          <td colspan="4">Total em doses</td>
          <td style="text-align:right">${doses(total)}</td>
        </tr>
      </tfoot>
    </table>`;
};

const secaoExtrato = (dados) => {
  const linhas = extratoDeEstoque(dados);
  if (linhas.length === 0) return '';

  const sinal = (l) => (l.tipo === 'entrada' ? '+' : '−');
  const quanto = (l) => (l.material === 'semen'
    ? `${doses(l.quantidade)} dose${l.quantidade === 1 ? '' : 's'}`
    : `${num(l.quantidade)} palheta${l.quantidade === 1 ? '' : 's'}`);

  return `
    <div class="section-title" style="margin-top:18px">Extrato</div>
    <table>
      <thead>
        <tr>
          <th>Data</th><th>Movimento</th><th>Material</th>
          <th style="text-align:right">Quantidade</th><th>Destino</th>
        </tr>
      </thead>
      <tbody>
        ${linhas.map(l => `
          <tr>
            <td style="white-space:nowrap">${l.data ? fmt(l.data) : '—'}${l.aproximada ? ' *' : ''}</td>
            <td class="${l.tipo === 'entrada' ? 'ok' : ''}">${escapa(l.titulo)}</td>
            <td>${escapa(l.descricao || '—')}</td>
            <td style="text-align:right;white-space:nowrap">${sinal(l)} ${quanto(l)}</td>
            <td class="dim">${escapa(l.detalhe || '—')}</td>
          </tr>`).join('')}
      </tbody>
    </table>
    ${temDataAproximada(linhas) ? `
      <p style="font-size:10px;color:#64748b;margin-top:6px">
        * Data do serviço que produziu o embrião, usada porque a data do
        congelamento não foi registrada para essa palheta.
      </p>` : ''}
    ${extratoFecha(dados) ? '' : `
      <p style="font-size:10px;color:#64748b;margin-top:6px">
        O extrato não fecha por subtração com o saldo acima: a palheta que já saiu
        do botijão aparece na saída, e a data em que ela foi congelada sai junto
        com ela. O saldo de cima é contado do botijão, e não desta lista.
      </p>`}`;
};

/**
 * Gera o extrato de estoque de um cliente.
 *
 * `dados` é o que `estoqueDoCliente` devolve, cru. A folha se monta do mesmo
 * material que a tela mostra — se as duas divergissem, a que vai para o cliente
 * é a que ninguém confere.
 */
export const gerarExtratoDeEstoquePDF = (dados, { cliente, laboratorio } = {}) => {
  const r = resumoDoEstoqueDoCliente(dados);

  if (r.vazio && (dados?.saidas || []).length === 0 && (dados?.movimentos || []).length === 0) {
    alert('Este cliente não tem nada guardado nem movimentação registrada.');
    return;
  }

  const titulo = `Estoque — ${cliente?.name || 'Cliente'}`;
  const html = `
    <style>${baseCSS()}</style>
    ${cabecalho(cliente, laboratorio)}

    <div class="session-info" style="border-top:1px solid #e2e8f0;margin-bottom:14px">
      <span><b>${num(r.embrioes)}</b> ${r.embrioes === 1 ? 'embrião' : 'embriões'} no botijão</span>
      <span><b>${doses(r.doses)}</b> dose${r.doses === 1 ? '' : 's'} de sêmen</span>
      ${r.cruzamentos > 0 ? `<span>${num(r.cruzamentos)} cruzamento(s)</span>` : ''}
      ${r.partidas > 0 ? `<span>${num(r.partidas)} partida(s)</span>` : ''}
    </div>

    ${r.vazio
      ? '<p style="font-size:12px;color:#64748b">Nada guardado no laboratório hoje.</p>'
      : `${secaoEmbrioes(dados?.congelados || [])}${secaoSemen(dados?.partidas || [])}`}

    ${secaoExtrato(dados)}

    <div class="footer">
      Para retirar ou transferir material, fale com o laboratório.
      ${linhaDoResponsavel()}
    </div>`;

  openPrint(titulo, html);
};
